import axios from 'axios';
import { API_CONFIG, getHeaders } from './config';

// Public game-tracker API (Cloudflare Worker reads precomputed ga_games/ga_meta).
const client = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: getHeaders(),
});

export type Win = 1 | 7 | 30;

// One game's aggregates for a single window (snake_case from D1).
export interface GARow {
  slug: string;
  win: number;
  rnk: number;
  game_id: string | null;
  name: string;
  box_art_url: string | null;
  genre: string | null; // primary gameplay-genre label for display
  genres: string | null; // full official genre membership list, ", "-joined
  steam_appid: number | null;
  steam_name: string | null;
  watch_hrs: number | null;
  avg_channels: number | null;
  peak_channels: number | null;
  avg_viewers: number | null;
  peak_viewers: number | null;
  change_pct: number | null;
  ccu: number | null;
  reviews: number | null;
  pct_positive: number | null;
  est_sales: number | null;
  price: number | null; // cents
  discount: number | null; // percent
  has_steam_stats: number;
  updated_at: string;
}

export interface GAListResponse {
  win: number;
  sort: string;
  dir: string;
  genre: string | null;
  q: string | null;
  steam: boolean;
  page: number;
  pageSize: number;
  total: number;
  rows: GARow[];
}

export interface GAMetaInfo {
  updated_at: string;
  games_1: number;
  games_7: number;
  games_30: number;
  steam_latest: string;
  note: string;
}

export interface GAMeta {
  meta: GAMetaInfo | null;
  win: number;
  genres: { genre: string; n: number }[];
}

export interface GAGame {
  game: {
    slug: string;
    name: string;
    genre: string | null;
    box_art_url: string | null;
    game_id: string | null;
    steam_appid: number | null;
    steam_name: string | null;
  };
  windows: Record<string, GARow>;
}

export interface GAListParams {
  win?: number;
  genre?: string;
  q?: string;
  steam?: 1; // only games with a verified Steam store page
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export const gameAnalyticsApi = {
  async list(params: GAListParams = {}): Promise<GAListResponse> {
    const response = await client.get('/game-analytics', { params });
    return response.data;
  },
  async meta(win?: number): Promise<GAMeta> {
    const response = await client.get('/game-analytics/meta', {
      params: win ? { win } : {},
    });
    return response.data;
  },
  async game(slug: string): Promise<GAGame> {
    const response = await client.get(`/game-analytics/${encodeURIComponent(slug)}`);
    return response.data;
  },
};
